import React, { useState, useEffect, useRef } from "react"
import classnames from "classnames"
import * as layouts from "layouts"
import * as sessions from "sessions"
import * as metasearch from "metasearch"
import * as uuid from "uuid"
import * as brands from "brands"
import * as api from "./api"
import * as httpx from "httpx"
import * as errors from "errors"
import * as timex from "timex"
import { SearchRequest } from "./brandguard.metrics_pb"
import { Bar } from "charts"
import * as icons from "icons"
import * as inputs from "inputs"
import * as metricsLayouts from "./layouts"
import * as filters from "./filters"
import { getDaysArray, toDayKey, capitalize, INFISO, NEGINFISO } from "./dates"

const eventTypes = ["image", "text", "document", "video"] as const

const typeColors: Record<string, { base: string; alpha: string }> = {
	image: layouts.theme.colors.charts.blue,
	text: layouts.theme.colors.charts.green,
	document: layouts.theme.colors.charts.yellow,
	video: layouts.theme.colors.charts.violet,
}

interface typeAggregate {
	onbrand: number
	offbrand: number
	byDayOnbrand: { [date: string]: number }
	byDayOffbrand: { [date: string]: number }
}

interface parsed {
	labels: string[]
	types: Record<string, typeAggregate>
	totalOnbrand: number
	totalOffbrand: number
}

function emptyAggregate(): typeAggregate {
	return {
		onbrand: 0,
		offbrand: 0,
		byDayOnbrand: {},
		byDayOffbrand: {},
	}
}

function parsedInitial(): parsed {
	const types: Record<string, typeAggregate> = {}
	eventTypes.forEach((t) => {
		types[t] = emptyAggregate()
	})
	return { labels: [], types, totalOnbrand: 0, totalOffbrand: 0 }
}

function parse(r: api.AssetTypeMetricSearchResponse, dr: metasearch.DateRange, uid: string = uuid.NIL): parsed {
	const data = parsedInitial()

	getDaysArray(new Date(dr.oldest), new Date(dr.newest)).forEach((i) => {
		const day = toDayKey(i)
		data.labels.push(day)
		eventTypes.forEach((t) => {
			data.types[t].byDayOnbrand[day] = 0
			data.types[t].byDayOffbrand[day] = 0
		})
	})

	;(r.items || []).forEach((e) => {
		if (uid !== uuid.NIL && e.profile_id !== uid) return
		const day = toDayKey(new Date(e.range!.oldest))
		const t = e.event_type
		if (!data.types[t]) return

		const approved = Number(BigInt(e.approved_count || 0))
		const rejected = Number(BigInt(e.rejected_count || 0))

		data.types[t].onbrand += approved
		data.types[t].offbrand += rejected
		data.types[t].byDayOnbrand[day] = (data.types[t].byDayOnbrand[day] || 0) + approved
		data.types[t].byDayOffbrand[day] = (data.types[t].byDayOffbrand[day] || 0) + rejected
		data.totalOnbrand += approved
		data.totalOffbrand += rejected
	})

	return data
}

export default function Display(): JSX.Element {
	const brand = brands.caching.useCached()
	const bearertoken = sessions.useToken()

	const [loading, setLoading] = useState(true)
	const [cause, setCause] = useState(undefined as JSX.Element | undefined)

	const [req, setReq] = useState(
		api.SearchZero({
			brand_id: brand.id,
			range: metasearch.dateranges.days(30),
		}) as SearchRequest,
	)

	const [chartData, setChartData] = useState(parsedInitial())
	const [currentUser, setCurrentUser] = useState(filters.zeroUser)
	const [response, setResponse] = useState({} as api.AssetTypeMetricSearchResponse)
	const currentUserRef = useRef(currentUser)
	currentUserRef.current = currentUser

	const updateCurrentUser = (user: filters.user): void => {
		setCurrentUser(user)
		setChartData(parse(response, req.range!, user.key))
	}

	useEffect(() => {
		if (req.brand_id === uuid.NIL || req.range?.oldest === NEGINFISO || req.range?.newest === INFISO) return
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.search_training(req, bearertoken))
			.then((resp) => {
				setResponse(resp)
				setChartData(parse(resp, req.range!, currentUserRef.current.key))
				setCause(undefined)
			})
			.catch(httpx.errors.cancellation(() => undefined))
			.catch(
				httpx.errors.forbidden((cause) => {
					console.warn("insufficient privileges unable to view metrics", cause)
					setCause(
						<layouts.containers.box styled m="auto">
							<errors.Textual>you do not have permission to view metrics</errors.Textual>
						</layouts.containers.box>,
					)
				}),
			)
			.catch((c: unknown) => {
				console.error("unable to retrieve metrics", c)
				setCause(
					<layouts.containers.box styled m="auto" width="100%" height="100%">
						<errors.Textual onClick={() => setCause(undefined)}>unable to retrieve metrics</errors.Textual>
					</layouts.containers.box>,
				)
			})
			.finally(() => setLoading(false))
		return p.cancel
	}, [req])

	const stackedOptions = {
		maintainAspectRatio: true,
		scales: {
			x: {
				stacked: true,
				ticks: {
					color: layouts.theme.colors.grey.light30,
				},
				grid: {
					display: false,
				},
			},
			y: {
				stacked: true,
				type: "linear" as const,
				display: true,
				position: "left" as const,
				min: 0,
				ticks: {
					color: layouts.theme.colors.grey.light30,
					maxTicksLimit: 5,
				},
				title: {
					display: true,
					text: "Training Volume",
					color: layouts.theme.colors.grey.light30,
					font: { size: 14 },
					padding: 0,
					align: "center" as const,
				},
				grid: {
					display: false,
				},
			},
		},
		animation: false as const,
		layout: { padding: 20 },
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				enabled: true,
				mode: "nearest" as const,
				intersect: true,
			},
		},
	}

	const onbrandDatasets = eventTypes.map((t) => ({
		label: `${capitalize(t)} (Onbrand)`,
		data: chartData.labels.map((day) => chartData.types[t].byDayOnbrand[day] || 0),
		backgroundColor: typeColors[t].base,
		borderColor: typeColors[t].base,
		borderWidth: 1,
		stack: "Onbrand",
	}))

	const offbrandDatasets = eventTypes.map((t) => ({
		label: `${capitalize(t)} (Offbrand)`,
		data: chartData.labels.map((day) => chartData.types[t].byDayOffbrand[day] || 0),
		backgroundColor: typeColors[t].alpha,
		borderColor: typeColors[t].base,
		borderWidth: 1,
		stack: "Offbrand",
	}))

	const stackedData = {
		labels: chartData.labels,
		datasets: [...onbrandDatasets, ...offbrandDatasets],
	}

	return (
		<layouts.loading.screen loading={loading} height="100%">
			<errors.overlay cause={cause} flexDirection="column" flex="1">
				<layouts.containers.flex>
					<inputs.calendars.DateRangeOneDatepicker
						className={metricsLayouts.StyledDatePicker}
						placeholderText="Date Range"
						minStartDate={new Date(timex.from.iso(req.range?.oldest).minus(timex.duration.days(180)).toISO())}
						maxEndDate={new Date()}
						startDate={new Date(req.range?.oldest || NEGINFISO)}
						endDate={new Date(req.range?.newest || INFISO)}
						changeStart={(d: Date) => {
							setReq((prevState) => ({ ...prevState, range: { newest: INFISO, oldest: timex.from.date(d).toISO() } }))
						}}
						changeEnd={(d: Date) => {
							setReq((prevState) => ({
								...prevState,
								range: { ...prevState.range, newest: timex.from.date(d).toISO() },
							}))
						}}
					/>

					<layouts.containers.flex mt="3px" ml="10px" fontWeight="600">
						{req.range?.oldest !== NEGINFISO &&
							req.range?.newest !== INFISO &&
							`${timex.from
								.iso(req.range?.newest)
								.diff(timex.from.iso(req.range?.oldest), "days")
								.days.toFixed()} day(s)`}
					</layouts.containers.flex>
					<filters.ApprovalFilter
						req={req}
						setReq={setReq}
						currentUser={currentUser}
						setCurrentUser={updateCurrentUser}
					/>
				</layouts.containers.flex>

				<layouts.containers.box
					className="metrics"
					width="100%"
					background={layouts.theme.colors.white}
					overflowY="auto"
				>
					<layouts.containers.box className={classnames("primary", metricsLayouts.hover)} height="512px" maxHeight="512px">
						<Bar options={stackedOptions} data={stackedData} width={100} />
					</layouts.containers.box>
					<layouts.containers.flex justifyContent="center">
						<metricsLayouts.Card
							flex="1"
							maxWidth="49%"
							minHeight="230px"
							maxHeight="230px"
							padding="20px"
							mr="1px"
							textAlign="center"
							flexDirection="column"
						>
							<metricsLayouts.Header mb="15px">Onbrand</metricsLayouts.Header>
							<layouts.containers.flex>
								<layouts.containers.flex pr="10px">
									<icons.ImpressionsEye
										fill={layouts.theme.colors.charts.blue.base}
										width="30px"
										height="30px"
										verticalAlign="middle"
									/>
								</layouts.containers.flex>
								<layouts.containers.flex fontSize="30px" verticalAlign="middle">
									{String(chartData.totalOnbrand)}
								</layouts.containers.flex>
							</layouts.containers.flex>
							<layouts.containers.flex flexDirection="column" mt="15px">
								{eventTypes.map((t) => (
									<layouts.containers.flex key={t} justifyContent="space-between" mb="5px">
										<layouts.containers.span color={layouts.theme.colors.grey.medium} fontSize="12px">
											{capitalize(t)}
										</layouts.containers.span>
										<layouts.containers.span fontWeight="600" fontSize="12px">
											{chartData.types[t].onbrand}
										</layouts.containers.span>
									</layouts.containers.flex>
								))}
							</layouts.containers.flex>
							<metricsLayouts.Header py="15px" color={layouts.theme.colors.grey.dark10}>
								Total Onbrand
							</metricsLayouts.Header>
						</metricsLayouts.Card>
						<metricsLayouts.Card
							flex="1"
							maxWidth="49%"
							minHeight="230px"
							maxHeight="230px"
							padding="20px"
							mr="1px"
							textAlign="center"
							flexDirection="column"
						>
							<metricsLayouts.Header mb="15px">Offbrand</metricsLayouts.Header>
							<layouts.containers.flex>
								<layouts.containers.flex pr="10px">
									<icons.ImpressionsEye
										fill={layouts.theme.colors.charts.green.base}
										width="30px"
										height="30px"
										verticalAlign="middle"
									/>
								</layouts.containers.flex>
								<layouts.containers.flex fontSize="30px" verticalAlign="middle">
									{String(chartData.totalOffbrand)}
								</layouts.containers.flex>
							</layouts.containers.flex>
							<layouts.containers.flex flexDirection="column" mt="15px">
								{eventTypes.map((t) => (
									<layouts.containers.flex key={t} justifyContent="space-between" mb="5px">
										<layouts.containers.span color={layouts.theme.colors.grey.medium} fontSize="12px">
											{capitalize(t)}
										</layouts.containers.span>
										<layouts.containers.span fontWeight="600" fontSize="12px">
											{chartData.types[t].offbrand}
										</layouts.containers.span>
									</layouts.containers.flex>
								))}
							</layouts.containers.flex>
							<metricsLayouts.Header py="15px" color={layouts.theme.colors.grey.dark10}>
								Total Offbrand
							</metricsLayouts.Header>
						</metricsLayouts.Card>
					</layouts.containers.flex>
				</layouts.containers.box>
			</errors.overlay>
		</layouts.loading.screen>
	)
}
